import { useQuery } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { useNotify, type Identifier } from "ra-core";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

import { getSupabaseClient } from "../../providers/supabase/supabase";
import type { GulaClient } from "../gulaTypes";
import { isTrashable, trashLead } from "../leadTrash";

/**
 * "Eliminar" (admins): deletes the conversation from Mensajes. The lead stays
 * unless they also throw it in the trash (only direct-message leads).
 */
export const DeleteChatButton = ({
  name,
  waContactId,
  socialThreadId,
  clientId,
  onDeleted,
}: {
  name: string;
  /** WhatsApp conversation (by contact) */
  waContactId?: Identifier | null;
  /** Instagram / Messenger conversation */
  socialThreadId?: Identifier | null;
  clientId?: Identifier | null;
  onDeleted: () => void;
}) => {
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [alsoLead, setAlsoLead] = useState(false);

  const { data: lead } = useQuery({
    queryKey: ["gula-delete-chat-lead", clientId],
    enabled: open && clientId != null,
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("gula_clients_summary")
        .select("*")
        .eq("id", Number(clientId))
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as GulaClient | null;
    },
  });
  const canTrashLead = !!lead && isTrashable(lead);

  const remove = async () => {
    setBusy(true);
    try {
      const { error } = await getSupabaseClient().rpc("gula_delete_chat", {
        p_wa_contact_id: waContactId == null ? null : Number(waContactId),
        p_social_thread_id:
          socialThreadId == null ? null : Number(socialThreadId),
      });
      if (error) throw new Error(error.message);
      if (alsoLead && canTrashLead && lead) await trashLead(lead);
      notify(
        alsoLead && canTrashLead
          ? `Conversación eliminada y lead a la basura (${name})`
          : `Conversación con ${name} eliminada`,
        { type: "success" },
      );
      setOpen(false);
      onDeleted();
    } catch (error: unknown) {
      notify(
        error instanceof Error ? error.message : "No se ha podido eliminar",
        { type: "error" },
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setAlsoLead(false);
          setOpen(true);
        }}
        className="inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-medium text-red-600 hover:border-red-300 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-400/15"
        title="Eliminar esta conversación del CRM"
      >
        <Trash2 className="h-3.5 w-3.5" />
        Eliminar
      </button>
      <Dialog open={open} onOpenChange={(value) => !busy && setOpen(value)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Eliminar la conversación con {name}?</DialogTitle>
            <DialogDescription asChild>
              <div className="flex flex-col gap-2 text-sm">
                <p>
                  Se borran del CRM todos sus mensajes y desaparece de Mensajes
                  y de su ficha. No se puede deshacer.
                </p>
                <p>
                  En la app de WhatsApp Business (o en Instagram / Facebook) no
                  cambia nada. Si vuelve a escribir, el chat aparece otra vez
                  solo con los mensajes nuevos.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          {canTrashLead ? (
            <div className="flex items-start gap-2 rounded-xl border p-3">
              <Checkbox
                id="delete-chat-lead"
                checked={alsoLead}
                onCheckedChange={(value) => setAlsoLead(value === true)}
              />
              <Label
                htmlFor="delete-chat-lead"
                className="flex flex-col items-start gap-0.5 font-normal"
              >
                <span className="font-medium">
                  Tirar también el lead a la basura
                </span>
                <span className="text-xs text-muted-foreground">
                  Sale del tablero y, si vuelve a escribir, no entra como lead.
                  Se puede recuperar desde Clientes → Basura.
                </span>
              </Label>
            </div>
          ) : null}
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setOpen(false)}
              disabled={busy}
            >
              Cancelar
            </Button>
            <Button variant="destructive" onClick={remove} disabled={busy}>
              <Trash2 className="h-4 w-4" />
              {busy ? "Eliminando…" : "Eliminar conversación"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
